/**
 * Consentimento OAuth do Agent Gateway (AGENT GATEWAY V1).
 *
 * É o "authorization endpoint" visto pelo usuário: o conector externo (ex.: o
 * futuro Ceres Assistant no ChatGPT) abre esta rota com os parâmetros OAuth;
 * quem não tem sessão cai no login normal (senha + MFA + aceite) e volta.
 * Aprovar chama a API — que valida cliente, redirect EXATA e a permissão
 * `agent:connector` — e o navegador segue para a redirect_uri com o código.
 *
 * Fora do AppShell de propósito, como /aceite: esta tela tem UMA decisão.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BRANDING } from '../config/branding';
import { ApiError, api, toQuery } from '../lib/api';
import { t } from '../lib/i18n';
import { ErrorState, Spinner } from '../components/ui';
import { HelpPanel } from '../components/help';

interface Contexto {
  clientName: string;
  scopes: string[];
  redirectHost: string;
  podeConectar: boolean;
}

const ROTULO_DO_ESCOPO: Record<string, string> = {
  'crm:read': 'Consultar seus dados comerciais (empresas, contatos, oportunidades, catálogo, atividades)',
  'crm:write': 'Propor alterações em seu nome — sempre com prévia e confirmação sua',
};

export function ConectarAgentePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const [erro, setErro] = useState<unknown>(null);
  const [decidindo, setDecidindo] = useState(false);

  // O conector fala snake_case (OAuth); a API interna fala o contrato do CRM.
  const pedido = useMemo(
    () => ({
      clientId: searchParams.get('client_id') ?? '',
      redirectUri: searchParams.get('redirect_uri') ?? '',
      scope: searchParams.get('scope') ?? undefined,
      state: searchParams.get('state') ?? undefined,
      codeChallenge: searchParams.get('code_challenge') ?? undefined,
      codeChallengeMethod: searchParams.get('code_challenge_method') ?? undefined,
    }),
    [searchParams],
  );

  const contexto = useQuery({
    queryKey: ['oauth-contexto', pedido],
    queryFn: () => api.get<Contexto>(`/oauth/authorize/contexto${toQuery(pedido)}`),
    enabled: pedido.clientId !== '' && pedido.redirectUri !== '',
    retry: false,
  });

  const decidir = async (aprovado: boolean): Promise<void> => {
    setErro(null);
    setDecidindo(true);
    try {
      const resposta = await api.post<{ redirectTo: string }>('/oauth/authorize/decisao', {
        ...pedido,
        aprovado,
      });
      window.location.assign(resposta.redirectTo);
    } catch (capturado) {
      setErro(capturado);
      setDecidindo(false);
    }
  };

  const parametrosAusentes = pedido.clientId === '' || pedido.redirectUri === '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-ink">{t('conectar.titulo')}</h1>
          <p className="mt-0.5 text-sm text-ink-muted">{t('conectar.texto')}</p>
        </div>

        <div className="panel px-5 py-5">
          {parametrosAusentes ? (
            <p className="text-sm text-ink-muted">{t('conectar.semParametros')}</p>
          ) : contexto.isPending ? (
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Spinner /> {t('conectar.carregando')}
            </div>
          ) : contexto.isError ? (
            <ErrorState error={contexto.error} onRetry={() => void contexto.refetch()} />
          ) : (
            <div className="space-y-4">
              <p className="text-base text-ink">
                <strong>{contexto.data.clientName}</strong> {t('conectar.pedeAcesso')}
              </p>

              <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink-muted">
                {contexto.data.scopes.map((escopo) => (
                  <li key={escopo}>{ROTULO_DO_ESCOPO[escopo] ?? escopo}</li>
                ))}
              </ul>

              <HelpPanel title={t('conectar.oQueSignifica')} tone="warning">
                {t('conectar.aviso')}
              </HelpPanel>

              {!contexto.data.podeConectar && (
                <p className="rounded border border-line bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
                  {t('conectar.semPermissao')}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary flex-1"
                  disabled={decidindo}
                  onClick={() => void decidir(false)}
                >
                  {t('conectar.recusar')}
                </button>
                <button
                  type="button"
                  className="btn-primary flex-1"
                  data-testid="autorizar-conector"
                  disabled={decidindo || !contexto.data.podeConectar}
                  onClick={() => void decidir(true)}
                >
                  {decidindo && <Spinner />}
                  {t('conectar.autorizar')}
                </button>
              </div>

              <p className="text-xs text-ink-subtle">
                {t('conectar.destinoAviso')} <code>{contexto.data.redirectHost}</code>.
              </p>

              {erro !== null && (
                <p className="text-sm text-danger-700">
                  {erro instanceof ApiError ? erro.mensagemHumana : t('conectar.erroGenerico')}
                </p>
              )}
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-2xs text-ink-subtle">{BRANDING.legalLine}</p>
      </div>
    </div>
  );
}
